import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import ServiceChat from '../../components/ServiceChat';
import { PaymentStatusCard } from '../../components/PaymentStatus';
import { ProviderPrepVideos } from '../../components/TroubleshootingVideos';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ArrowLeft, Phone, Navigation, MapPin, User,
  Truck, Battery, Car, Key, Wrench, Shield, Loader2, CheckCircle, DollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const mechanicIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const customerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const serviceIcons = { tow: Truck, jump_start: Battery, flat_tire: Car, lockout: Key, repair: Wrench, diagnostic: Shield };
const statusFlow = ['accepted', 'en_route', 'arrived', 'in_progress', 'completed'];
const statusLabels = { accepted: "I've Arrived", en_route: "I've Arrived", arrived: 'Start Service', in_progress: 'Complete Job', completed: 'Done' };
const stepLabels = { accepted: 'Head to Location', en_route: 'On the Way', arrived: 'At Location', in_progress: 'Service in Progress', completed: 'Completed' };

export default function JobDetails() {
  const { requestId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [myLocation, setMyLocation] = useState(null);
  const [videosDismissed, setVideosDismissed] = useState(false);

  useEffect(() => {
    fetchRequest();
    startLocationTracking();
    const interval = setInterval(fetchRequest, 5000);
    return () => clearInterval(interval);
    // Intentional: fetchRequest is stable per requestId; we re-run when
    // requestId changes via the dep below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const res = await axios.get(`${API}/services/${requestId}`, { headers: getAuthHeaders(), withCredentials: true });
      setRequest(res.data);
    } catch { toast.error('Failed to load job'); }
    setLoading(false);
  };

  const startLocationTracking = () => {
    if (!navigator.geolocation) { setMyLocation({ lat: 40.7128, lng: -74.006 }); return; }
    navigator.geolocation.watchPosition(
      (p) => { const loc = { lat: p.coords.latitude, lng: p.coords.longitude }; setMyLocation(loc); updateMyLoc(loc); },
      () => setMyLocation({ lat: 40.7128, lng: -74.006 }),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 30000 }
    );
  };

  const updateMyLoc = async (loc) => {
    try { await axios.put(`${API}/mechanics/location`, { location: loc }, { headers: getAuthHeaders(), withCredentials: true }); }
    catch (err) { console.warn('Mechanic location update failed:', err?.message || err); }
  };

  const handleStatusUpdate = async () => {
    const idx = statusFlow.indexOf(request.status);
    if (idx === -1 || idx >= statusFlow.length - 1) return;
    const next = statusFlow[idx + 1];
    setUpdating(true);
    try {
      await axios.put(`${API}/services/${requestId}/status`, { status: next, mechanic_location: myLocation }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`Status updated to ${next.replace('_', ' ')}`);
      if (next === 'completed') setTimeout(() => navigate('/provider'), 2000);
      else fetchRequest();
    } catch { toast.error('Failed to update status'); }
    setUpdating(false);
  };

  const openNavigation = () => {
    if (request?.location) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${request.location.lat},${request.location.lng}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-[70vh]">
          <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#FF3008' }} />
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <h2 className="text-xl font-bold text-[#191919] mb-4">Job Not Found</h2>
          <Button onClick={() => navigate('/provider')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const ServiceIcon = serviceIcons[request.service_type] || Car;
  const stepIdx = statusFlow.indexOf(request.status);
  const isCompleted = request.status === 'completed';
  const isActive = ['accepted', 'en_route', 'arrived', 'in_progress'].includes(request.status);
  const providerPay = request.provider_pay || ((request.total_price || request.estimated_price) * 0.30);

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="job-details-page">
      <Header />

      {/* Map Section */}
      <div className="relative" style={{ height: '38vh' }}>
        {request.location?.lat && (
          <MapContainer
            center={[request.location.lat, request.location.lng]}
            zoom={14}
            className="h-full w-full"
            style={{ zIndex: 0 }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[request.location.lat, request.location.lng]} icon={customerIcon}>
              <Popup>Customer: {request.customer_name}</Popup>
            </Marker>
            {myLocation && (
              <>
                <Marker position={[myLocation.lat, myLocation.lng]} icon={mechanicIcon}>
                  <Popup>Your Location</Popup>
                </Marker>
                <Polyline
                  positions={[[myLocation.lat, myLocation.lng], [request.location.lat, request.location.lng]]}
                  color="#FF3008"
                  weight={4}
                  opacity={0.7}
                  dashArray="10, 10"
                />
              </>
            )}
          </MapContainer>
        )}

        {/* Navigate FAB */}
        {isActive && (
          <button
            onClick={openNavigation}
            className="absolute bottom-4 right-4 z-[1000] flex items-center gap-2 px-5 py-3 rounded-full text-white font-semibold shadow-lg"
            style={{ background: '#FF3008' }}
            data-testid="navigate-btn"
          >
            <Navigation className="w-4 h-4" />
            Navigate
          </button>
        )}

        {/* Back button */}
        <button
          onClick={() => navigate('/provider')}
          className="absolute top-4 left-4 z-[1000] w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-5 h-5 text-[#191919]" />
        </button>
      </div>

      {/* Details Panel */}
      <div className="bg-white rounded-t-3xl -mt-4 relative z-10 min-h-[50vh]">
        <div className="max-w-3xl mx-auto px-5 py-5">

          {/* Prep Videos */}
          {!isCompleted && !videosDismissed && ['accepted', 'en_route'].includes(request.status) && (
            <ProviderPrepVideos
              serviceType={request.service_type}
              serviceName={request.service_type?.replace('_', ' ')}
              isVisible={true}
              onDismiss={() => setVideosDismissed(true)}
              autoExpand={request.status === 'accepted'}
            />
          )}

          {/* Completion Banner */}
          {isCompleted && (
            <div className="bg-[#E6F9F3] rounded-2xl p-6 text-center mb-5" data-testid="completion-banner">
              <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: '#00B37E' }} />
              <h2 className="text-xl font-bold" style={{ color: '#00B37E' }}>Job Complete!</h2>
              <p className="text-[#00B37E]/70 text-sm mt-1">Great work! Earnings added to your account.</p>
              <p className="text-2xl font-extrabold mt-3" style={{ color: '#00B37E' }}>+${providerPay.toFixed(2)}</p>
            </div>
          )}

          {/* Step Progress */}
          {isActive && (
            <div className="flex items-center justify-center gap-1 mb-5 px-8">
              {statusFlow.slice(0, 4).map((s, i) => (
                <div key={s} className="flex items-center flex-1">
                  <div className={`w-3 h-3 rounded-full ${i <= stepIdx ? 'bg-[#FF3008]' : 'bg-[#E8E8E8]'}`} />
                  {i < 3 && <div className={`flex-1 h-[3px] mx-1 ${i < stepIdx ? 'bg-[#FF3008]' : 'bg-[#E8E8E8]'}`} />}
                </div>
              ))}
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#FF3008' }}>
                {isCompleted ? 'Completed' : stepLabels[request.status] || request.status}
              </p>
              <h1 className="text-2xl font-bold text-[#191919] capitalize mt-1">
                {request.service_type?.replace(/_/g, ' ')}
              </h1>
            </div>
            <div className="bg-[#F5F5F5] px-4 py-2 rounded-full">
              <span className="text-xl font-extrabold text-[#191919]" data-testid="job-pay">${providerPay.toFixed(2)}</span>
            </div>
          </div>

          {/* Info Cards */}
          <div className="divide-y divide-[#F0F0F0]">
            {/* Location */}
            <div className="flex items-start gap-3 py-3">
              <div className="w-8 h-8 rounded-full bg-[#FFF0ED] flex items-center justify-center mt-0.5">
                <MapPin className="w-4 h-4" style={{ color: '#FF3008' }} />
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-[#AFAFAF] uppercase tracking-wider font-medium">Location</p>
                <p className="text-[#191919] mt-0.5">{request.location?.address || 'Location unavailable'}</p>
              </div>
            </div>

            {/* Customer */}
            <div className="flex items-start gap-3 py-3">
              <div className="w-8 h-8 rounded-full bg-[#E6F9F3] flex items-center justify-center mt-0.5">
                <User className="w-4 h-4" style={{ color: '#00B37E' }} />
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-[#AFAFAF] uppercase tracking-wider font-medium">Customer</p>
                <p className="text-[#191919] mt-0.5">{request.customer_name}</p>
              </div>
              {isActive && (
                <button
                  onClick={() => window.location.href = `tel:${request.customer_phone || '2602183536'}`}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold text-white"
                  style={{ background: '#00B37E' }}
                  data-testid="call-customer-btn"
                >
                  Call
                </button>
              )}
            </div>

            {/* Vehicle */}
            <div className="flex items-start gap-3 py-3">
              <div className="w-8 h-8 rounded-full bg-[#FFF7E6] flex items-center justify-center mt-0.5">
                <Car className="w-4 h-4 text-[#FFAA00]" />
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-[#AFAFAF] uppercase tracking-wider font-medium">Vehicle</p>
                <p className="text-[#191919] mt-0.5">
                  {request.vehicle_year} {request.vehicle_make} {request.vehicle_model}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          {request.description && (
            <div className="bg-[#F5F5F5] rounded-xl p-4 mt-3">
              <p className="text-[11px] text-[#AFAFAF] uppercase font-medium mb-1">Notes from customer</p>
              <p className="text-sm text-[#191919] leading-5">{request.description}</p>
            </div>
          )}

          {/* Chat */}
          {isActive && (
            <div className="mt-4">
              <ServiceChat
                requestId={requestId}
                otherPartyName={request.customer_name}
                getAuthHeaders={getAuthHeaders}
                userType="mechanic"
              />
            </div>
          )}

          {/* Payment Status */}
          <div className="mt-4" data-testid="payment-status-card">
            <PaymentStatusCard
              customerPaymentStatus={request.payment_status || 'pending'}
              providerPaymentStatus={request.provider_payment_status || 'pending'}
              customerTotal={request.total_price || request.estimated_price}
              providerPay={providerPay}
              showProviderInfo={true}
              userType="provider"
            />
          </div>

          {/* Action Button */}
          {isActive && (
            <div className="mt-6 pb-6">
              <button
                onClick={handleStatusUpdate}
                disabled={updating}
                className="w-full py-4 rounded-full text-white font-bold text-lg transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
                style={{ background: request.status === 'in_progress' ? '#00B37E' : '#FF3008' }}
                data-testid="update-status-btn"
              >
                {updating ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : statusLabels[request.status]}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
